import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api } from "@/lib/api";
import { when } from "@/lib/dates";
import { Icon } from "@/ui/icons";
import { Badge, Button, Card, cx, Empty, ErrorText } from "@/ui/kit";
import { PageHeader } from "@/ui/Shell";

interface Row {
  id: string;
  title: string;
  startsAt: string;
  venueName: string;
  status: string;
  capacity: number | null;
  going: number;
  maybe: number;
  waitlist: number;
  areas: string | null;
}

export default function Events() {
  const nav = useNavigate();
  const [scope, setScope] = useState<"upcoming" | "drafts" | "past">("upcoming");
  const q = useQuery({ queryKey: ["admin-events", scope], queryFn: () => api<Row[]>(`/v1/admin/events?scope=${scope}`) });
  return (
    <>
      <PageHeader title="Events" action={<Button onClick={() => nav("/events/new")} className="!px-4"><Icon name="plus" className="size-5" />New</Button>} />
      <div className="mb-4 flex rounded-full bg-mist p-1" role="tablist">
        {(["upcoming", "drafts", "past"] as const).map((s) => (
          <button key={s} role="tab" aria-selected={scope === s} onClick={() => setScope(s)} className={cx("min-h-10 flex-1 rounded-full text-sm capitalize", scope === s ? "bg-white font-bold shadow-sm" : "")}>
            {s}
          </button>
        ))}
      </div>
      <ErrorText error={q.error} />
      <div className="grid grid-cols-[minmax(0,1fr)] gap-2">
        {(q.data ?? []).map((e) => (
          <Card key={e.id} onClick={() => nav(`/events/${e.id}`)}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">{e.title}</p>
                <p className="text-sm text-muted">{when(e.startsAt)} · {e.venueName}</p>
                {e.areas ? (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {e.areas.split(", ").map((a) => (
                      <span key={a} className={cx("rounded-full px-2 py-0.5 text-xs", a === "All of Chicago" ? "bg-ink font-semibold text-white" : "bg-mist text-ink-soft")}>{a}</span>
                    ))}
                  </div>
                ) : null}
              </div>
              {e.status !== "published" ? <Badge tone={e.status === "draft" ? "butter" : "blush"}>{e.status}</Badge> : null}
            </div>
            <p className="mt-2 text-sm">
              <b>{e.going}</b> going{e.capacity ? ` of ${e.capacity}` : ""} · {e.maybe} maybe{e.waitlist ? ` · ${e.waitlist} waitlisted` : ""}
            </p>
          </Card>
        ))}
      </div>
      {q.isSuccess && !q.data.length ? <Empty title={scope === "drafts" ? "No drafts" : scope === "past" ? "No past events" : "No upcoming events"}>Tap “New” to plan one.</Empty> : null}
    </>
  );
}
